import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStudyGroupQuestions } from '../lib/api';
import { scrollAppToTop } from '../lib/scroll';

export function StudyGroupReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useStudyGroupQuestions(id!);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const questions: any[] = data?.questions ?? [];
  const question = questions[currentIndex];
  const total = questions.length;

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      scrollAppToTop();
    }
  };

  const handleNext = () => {
    if (currentIndex < total - 1) {
      setCurrentIndex(currentIndex + 1);
      scrollAppToTop();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-slate-500">
        Loading questions...
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="max-w-lg mx-auto pb-24 px-4">
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => navigate(`/study-groups/${id}/results`)}
            className="text-slate-500 hover:text-squishy-dark transition-colors"
            aria-label="Back"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-squishy-dark">Review</h1>
        </div>
        <div className="text-center py-12 text-slate-400 text-sm">
          No questions to review.
        </div>
      </div>
    );
  }

  // Normalized review shape from GET /study-groups/:id/questions.
  const qText: string = question?.text ?? '';
  const qOptions: string[] = question?.options ?? [];
  const qCorrectIndex: number = question?.correctIndex ?? 0;
  const qTopic: string = question?.topic ?? '';
  const isQuick: boolean = !!question?.isQuick;
  const qExplanation: string = isQuick ? '' : (question?.briefExplanation ?? '');
  const qProExplanation: string = isQuick ? '' : (question?.proExplanation ?? '');
  const players: Array<{ userId: string; displayName: string; chosenIndex: number | null; correct: boolean; isFirstCorrect: boolean }> = question?.players ?? [];

  return (
    <div className="max-w-lg mx-auto pb-24 px-4 space-y-4">
      {/* ── Header ────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/study-groups/${id}/results`)}
          className="text-slate-500 hover:text-squishy-dark transition-colors"
          aria-label="Back"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-squishy-dark">Review</h1>
          <p className="text-xs text-slate-500">
            Question {currentIndex + 1} of {total}
          </p>
        </div>
      </div>

      {/* ── Progress bar ──────────────────────────────────────── */}
      <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-squishy-primary rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / total) * 100}%` }}
        />
      </div>

      {/* ── Topic badge ───────────────────────────────────────── */}
      {qTopic && (
        <div className="inline-block bg-slate-100 text-slate-600 text-xs font-medium px-2.5 py-1 rounded-full">
          {qTopic}
        </div>
      )}

      {/* ── Question text ─────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-sm leading-relaxed text-slate-800">{qText}</p>
      </div>

      {/* ── Options ───────────────────────────────────────────── */}
      <div className="space-y-2">
        {qOptions.map((opt: string, i: number) => {
          const isCorrect = i === qCorrectIndex;

          let borderClass = 'border-slate-200 bg-white text-slate-600';
          if (isCorrect) {
            borderClass = 'border-emerald-400 bg-emerald-50 text-emerald-800 dark:text-emerald-300 font-medium';
          }

          return (
            <div
              key={i}
              className={`p-4 rounded-xl border text-sm ${borderClass}`}
            >
              <div className="flex items-start gap-2">
                <span className="font-semibold text-xs mt-0.5">
                  {String.fromCharCode(65 + i)}.
                </span>
                <span className="flex-1">{opt}</span>
                {isCorrect && (
                  <svg className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Who answered what ──────────────────────────────────── */}
      {players.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs font-semibold text-slate-500">Players</div>
          {players.map((p) => (
            <div key={p.userId} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
              <span className={`w-2 h-2 rounded-full ${p.correct ? 'bg-emerald-500' : 'bg-red-400'}`} />
              <span className="text-sm text-slate-700 flex-1 truncate">
                {p.displayName}
                {p.isFirstCorrect && <span className="ml-1" title="Answered correctly first">🏆</span>}
              </span>
              <span className="text-xs text-slate-400">
                {p.chosenIndex != null
                  ? `Chose ${String.fromCharCode(65 + p.chosenIndex)}`
                  : 'No answer'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Explanation (regular questions only) ───────────────── */}
      {qExplanation && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="text-xs font-semibold text-blue-700 mb-1">Explanation</div>
          <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed">{qExplanation}</p>
        </div>
      )}

      {/* ── Pro explanation (server returns it only for Pro viewers) ── */}
      {qProExplanation && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-xs font-bold text-amber-700">PRO</span>
            <span className="text-xs font-semibold text-amber-700">Deeper explanation</span>
          </div>
          <p className="text-sm text-amber-900 dark:text-amber-100 leading-relaxed whitespace-pre-wrap">{qProExplanation}</p>
        </div>
      )}

      {/* Quick-study questions intentionally show no explanations. */}
      {isQuick && (
        <p className="text-xs text-slate-400 text-center">Quick Study questions do not include explanations.</p>
      )}

      {/* ── Navigation ────────────────────────────────────────── */}
      <div className="flex gap-3 pt-2">
        <button
          onClick={handlePrev}
          disabled={currentIndex === 0}
          className="flex-1 bg-white border border-slate-200 text-slate-700 font-medium py-3 rounded-xl disabled:opacity-40 hover:border-slate-300 transition-colors text-sm"
        >
          Previous
        </button>
        <button
          onClick={handleNext}
          disabled={currentIndex >= total - 1}
          className="flex-1 bg-squishy-primary hover:bg-teal-600 text-white font-medium py-3 rounded-xl disabled:opacity-40 transition-colors text-sm"
        >
          Next
        </button>
      </div>
    </div>
  );
}
